/**
 * `ChannelDetailsPanel` — expanded per-row view for a CONNECTED channel.
 *
 * Backend contract: `channel-wizards-lexruntime-handback-2026-06-30.md`.
 * Reads: `GET /channels/:id/details`.
 * Actions:
 *   • `POST /channels/:id/reveal-secret` — show the raw bot token for
 *     30s (rate-limited 3 / 5 min per channel, audit-logged server-side).
 *     WhatsApp returns `secret: null` (multi-device sessions are opaque
 *     device-bound blobs — the panel surfaces the `note` instead).
 *   • `POST /channels/:id/test` — DM the most-recently-active peer.
 *   • Rotate → opens the wizard again (upsert semantics).
 *   • Disconnect → `POST /disconnect` (native semantics for Telegram —
 *     deletes bot-token.txt + stamps status; `requiresRoll: true` always).
 *
 * Loading + error UX inline; toasts for the terminal actions.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  Calendar,
  Check,
  Copy,
  Eye,
  EyeOff,
  Fingerprint,
  Loader2,
  RefreshCw,
  Send,
  Unlink,
  User,
} from "lucide-react";
import {
  disconnectChannel,
  getChannelDetails,
  revealChannelSecret,
  testChannel,
  LxOpenclawConfigError,
  type ChannelDetailsResponse,
  type ChannelRevealSecretResponse,
  type ChannelTestResponse,
  type ChannelId,
} from "@/api/lx-openclaw-config";
import { useToastStore } from "@/stores/useToastStore";
import { useOpenclawSettingsOptional } from "./OpenclawSettingsTabs";
import { ConfirmDestructiveDialog } from "@/components/ui/ConfirmDestructiveDialog";
import { cn } from "@/lib/utils";

export interface ChannelDetailsPanelProps {
  id: ChannelId;
  /** Open the pair-again wizard (rotate token). */
  onRotate: () => void;
  /**
   * Called after a successful disconnect so the HOST can re-read its own
   * state. Inside the settings shell this is unnecessary (the panel refreshes
   * the shell's context itself); outside it — the hero's channel dialog — the
   * host passes its own refetch. Both run when present.
   */
  onChanged?: () => void;
}

/**
 * The rotate/reconnect control's state — pure, and exported, because the
 * action row is UNREACHABLE by this repo's component tests: `details` loads in
 * an async effect and the panel returns early while loading (below), so
 * `renderToStaticMarkup` only ever captures the spinner. Keeping the decision
 * in a function makes the rule assertable instead of untested.
 *
 * - `"reconnect"` — TELEGRAM with no live connection. Its wizard is UPSERT
 *   (pair-or-rotate, idempotently), so this same control is the way back
 *   (ui-tester `I-rt1-20260911-1811-localdev-3`: a disconnect used to grey out
 *   the only control that could restore the channel).
 * - `"rotate"` — a connected channel: trading a working credential.
 * - `"locked"` — every OTHER channel with no live connection. `ChannelDetails-
 *   Panel`'s host passes one channel-agnostic `onRotate`, and e-mail is
 *   auto-provisioned with no connect flow at all while WhatsApp's re-pair path
 *   was not verified by that change — so both keep the prior behaviour rather
 *   than being offered a way back nobody specified.
 *
 * `status` is typed loosely on purpose: this decides presentation from whatever
 * the channel row reports, and only equality with `"connected"` matters.
 */
export function rotateControl(id: ChannelId, status: string | undefined): {
  state: "rotate" | "reconnect" | "locked";
  labelKey: "rotateToken" | "reconnect";
  disabled: boolean;
} {
  const connected = status === "connected";
  const state = connected ? "rotate" : id === "telegram" ? "reconnect" : "locked";
  return {
    state,
    labelKey: state === "reconnect" ? "reconnect" : "rotateToken",
    disabled: state === "locked",
  };
}

export function ChannelDetailsPanel({ id, onRotate, onChanged }: ChannelDetailsPanelProps) {
  const { t } = useTranslation();
  // Optional so this panel also renders OUTSIDE the settings shell (the hero's
  // per-channel dialog) — the same decoupling `TelegramSetupWizard` uses.
  const settings = useOpenclawSettingsOptional();
  const addToast = useToastStore((s) => s.addToast);
  const [details, setDetails] = useState<ChannelDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reveal, setReveal] = useState<ChannelRevealSecretResponse | null>(null);
  const [revealing, setRevealing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [lastTest, setLastTest] = useState<ChannelTestResponse | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);
  // Typed-phrase gate for disconnect (per operator directive — replaces
  // the prior window.confirm). Modeled on lexportal's SoftDeleteOrgDialog.
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const d = await getChannelDetails(id);
        if (!cancelled) setDetails(d);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Auto-hide the revealed secret when its expiresAt passes (30s window).
  useEffect(() => {
    if (!reveal?.expiresAt) return;
    const ms = new Date(reveal.expiresAt).getTime() - Date.now();
    if (ms <= 0) {
      setReveal(null);
      return;
    }
    const tid = window.setTimeout(() => setReveal(null), ms);
    return () => window.clearTimeout(tid);
  }, [reveal]);

  async function handleReveal() {
    if (revealing) return;
    setRevealing(true);
    try {
      const r = await revealChannelSecret(id, "user-copy");
      setReveal(r);
    } catch (err) {
      if (err instanceof LxOpenclawConfigError && err.code === "REVEAL_RATE_LIMITED") {
        addToast({
          kind: "error",
          text: t("settings.openclaw.channels.details.revealRateLimited"),
          autoDismissMs: 6000,
        });
      } else {
        addToast({
          kind: "error",
          text: err instanceof Error ? err.message : String(err),
          autoDismissMs: 4000,
        });
      }
    } finally {
      setRevealing(false);
    }
  }

  async function handleTest() {
    if (testing) return;
    setTesting(true);
    setTestError(null);
    try {
      const r = await testChannel(id);
      setLastTest(r);
      addToast({
        kind: "info",
        text: t("settings.openclaw.channels.details.testSent", { target: r.target }),
        autoDismissMs: 5000,
      });
    } catch (err) {
      const code = err instanceof LxOpenclawConfigError ? err.code : undefined;
      setTestError(
        code === "NO_TEST_TARGET"
          ? t("settings.openclaw.channels.details.noTestTarget")
          : err instanceof Error
            ? err.message
            : String(err),
      );
    } finally {
      setTesting(false);
    }
  }

  /** Fired ONLY after the ConfirmDestructiveDialog's typed-phrase gate
   *  passes (`I UNDERSTAND` / `EU ENTENDO` / `COMPRENDO`) — the button
   *  click below only OPENS the dialog; this handler is invoked by the
   *  dialog's `onConfirm`. */
  async function handleDisconnectConfirmed() {
    if (disconnecting) return;
    setDisconnecting(true);
    setDisconnectError(null);
    try {
      await disconnectChannel(id);
      await settings?.refresh();
      onChanged?.();
      setDisconnectDialogOpen(false);
      addToast({
        kind: "info",
        text: t("settings.openclaw.channels.details.disconnected"),
        autoDismissMs: 4000,
      });
    } catch (err) {
      // Keep the dialog open + show the error inline so the user can
      // retry without re-typing the phrase.
      setDisconnectError(err instanceof Error ? err.message : String(err));
    } finally {
      setDisconnecting(false);
    }
  }

  if (loading) {
    return (
      <div
        data-testid={`channel-details-${id}-loading`}
        className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] text-slate-500 dark:border-coffee-700/60 dark:bg-coffee-950/40 dark:text-cream-400"
      >
        <Loader2 size={11} aria-hidden className="animate-spin" />
        {t("settings.openclaw.channels.wizard.loading")}
      </div>
    );
  }
  if (error || !details) {
    return (
      <div
        data-testid={`channel-details-${id}-error`}
        className="flex items-center gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-800 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300"
      >
        <AlertTriangle size={11} aria-hidden />
        {error ?? t("settings.openclaw.channels.wizard.errorGeneric")}
      </div>
    );
  }

  const identity =
    details.channelId === "telegram" ? details.identity : undefined;
  const waIdentity =
    details.channelId === "whatsapp" ? details.identity : undefined;
  const fingerprint =
    details.channelId === "telegram" ? details.tokenFingerprint : undefined;
  // Decided once, read three times below (label, disabled, `data-state`) so the
  // three can never disagree about the same control.
  const rotate = rotateControl(id, details.status);

  return (
    <div
      data-testid={`channel-details-${id}`}
      className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-[12px] text-slate-700 dark:border-coffee-700/60 dark:bg-coffee-950/40 dark:text-cream-200"
    >
      <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1">
        {identity && (
          <>
            <FieldLabel Icon={User} label={t("settings.openclaw.channels.details.identity")} />
            <dd className="font-mono">
              @{identity.username}
              {identity.id ? (
                <span className="ml-2 text-slate-500 dark:text-cream-500">(id: {identity.id})</span>
              ) : null}
            </dd>
          </>
        )}
        {/* The paired line's number — absent for LID/anonymous ids, hence the
            separate profile-name row below rather than one blended field. */}
        {waIdentity?.phone && (
          <>
            <FieldLabel Icon={User} label={t("settings.openclaw.channels.details.phone")} />
            <dd className="font-mono">
              {waIdentity.phone}
              {waIdentity.deviceLabel && (
                <span className="ml-2 text-slate-500 dark:text-cream-500">({waIdentity.deviceLabel})</span>
              )}
            </dd>
          </>
        )}
        {waIdentity?.displayName && (
          <>
            <FieldLabel Icon={User} label={t("settings.openclaw.channels.details.identity")} />
            <dd>
              {waIdentity.displayName}
              {/* Only carry the device label here when there is no phone row to
                  hang it on, so it never renders twice. */}
              {!waIdentity.phone && waIdentity.deviceLabel && (
                <span className="ml-2 text-slate-500 dark:text-cream-500">({waIdentity.deviceLabel})</span>
              )}
            </dd>
          </>
        )}
        {fingerprint && (
          <>
            <FieldLabel Icon={Fingerprint} label={t("settings.openclaw.channels.details.fingerprint")} />
            <dd className="flex items-center gap-2">
              <span className="font-mono">{fingerprint}</span>
              <button
                type="button"
                onClick={handleReveal}
                disabled={revealing}
                data-testid={`channel-details-${id}-reveal`}
                className="inline-flex items-center gap-1 rounded border border-slate-300 px-1.5 py-0.5 text-[10px] text-slate-500 hover:bg-slate-100 disabled:opacity-50 dark:border-coffee-700 dark:text-cream-400 dark:hover:bg-coffee-800"
              >
                {reveal?.secret ? <EyeOff size={10} /> : <Eye size={10} />}
                {reveal?.secret
                  ? t("settings.openclaw.channels.details.hideSecret")
                  : t("settings.openclaw.channels.details.revealSecret")}
              </button>
            </dd>
          </>
        )}
        {reveal?.secret && (
          <>
            <dt className="text-slate-500 dark:text-cream-500">{t("settings.openclaw.channels.details.tokenFull")}</dt>
            <dd className="flex items-center gap-2">
              <code
                data-testid={`channel-details-${id}-secret`}
                className="max-w-full truncate rounded bg-slate-200 px-2 py-0.5 font-mono text-[11px] text-coffee-800 dark:bg-coffee-800 dark:text-cream-100"
              >
                {reveal.secret}
              </code>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard?.writeText(reveal.secret ?? "");
                  addToast({
                    kind: "info",
                    text: t("settings.openclaw.channels.details.copied"),
                    autoDismissMs: 2000,
                  });
                }}
                className="inline-flex items-center rounded border border-slate-300 p-1 text-slate-500 hover:bg-slate-100 dark:border-coffee-700 dark:text-cream-300 dark:hover:bg-coffee-800"
                title={t("settings.openclaw.channels.wizard.copy")}
              >
                <Copy size={11} />
              </button>
              <span className="text-[10px] italic text-slate-500 dark:text-slate-400">
                {t("settings.openclaw.channels.details.revealExpiresIn30s")}
              </span>
            </dd>
          </>
        )}
        {details.channelId === "whatsapp" && !waIdentity?.phone && !waIdentity?.displayName && (
          <>
            <FieldLabel Icon={AlertTriangle} label={t("settings.openclaw.channels.details.wa.pendingNativeTitle")} />
            <dd className="italic text-slate-500 dark:text-slate-400">
              {t("settings.openclaw.channels.details.wa.pendingNativeBody")}
            </dd>
          </>
        )}
        {details.connectedAt && (
          <>
            <FieldLabel Icon={Calendar} label={t("settings.openclaw.channels.details.connectedAt")} />
            <dd>{new Date(details.connectedAt).toLocaleString()}</dd>
          </>
        )}
        {details.lastSync && (
          <>
            <FieldLabel Icon={Calendar} label={t("settings.openclaw.channels.details.lastSync")} />
            <dd>{new Date(details.lastSync).toLocaleString()}</dd>
          </>
        )}
        {details.lastError && (
          <>
            <FieldLabel Icon={AlertTriangle} label={t("settings.openclaw.channels.details.lastError")} />
            <dd className="text-red-700 dark:text-red-300">{details.lastError}</dd>
          </>
        )}
      </dl>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 border-t border-slate-200/70 pt-2 dark:border-coffee-700/40">
        <button
          type="button"
          onClick={handleTest}
          disabled={testing || details.status !== "connected"}
          data-testid={`channel-details-${id}-test`}
          className="inline-flex items-center gap-1.5 rounded border border-gold-500/60 bg-gold-400/10 px-2 py-1 text-[11px] font-medium text-gold-700 hover:bg-gold-400/25 hover:text-gold-600 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gold-500/40 dark:text-gold-400"
        >
          {testing ? (
            <Loader2 size={11} aria-hidden className="animate-spin" />
          ) : (
            <Send size={11} aria-hidden />
          )}
          {t("settings.openclaw.channels.details.sendTest")}
        </button>
        {/* THE WAY BACK (ui-tester I-rt1-20260911-1811-localdev-3). `onRotate`
            reopens the setup wizard, which is UPSERT — pair-or-rotate,
            idempotently — so it IS the reconnect path. Disabling it on a
            disconnected channel removed the only control on this surface that
            could restore the channel, leaving "Desconectar" as the sole live
            action and no way back except the Agent tab.
            Enabled in both states for TELEGRAM; only the label changes, because
            "Trocar token" is a lie when there is no token left to trade.
            "Enviar teste" stays disabled — there is genuinely nothing to test.
            ⚠ TELEGRAM-ONLY, deliberately. `ChannelSettingsDialog` passes one
            channel-agnostic `onRotate`, so enabling this unconditionally would
            also offer "Reconectar" on EMAIL — which is auto-provisioned and has
            no connect flow at all — and on WhatsApp, whose re-pair path this
            change never verified. The operator's decision was Telegram's way
            back (RETEST-1 D6); every other channel keeps its prior behaviour. */}
        <button
          type="button"
          onClick={onRotate}
          disabled={rotate.disabled}
          data-testid={`channel-details-${id}-rotate`}
          data-state={rotate.state}
          className="inline-flex items-center gap-1.5 rounded border border-slate-300 px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-100 disabled:opacity-50 dark:border-coffee-700 dark:text-cream-300 dark:hover:bg-coffee-800"
        >
          <RefreshCw size={11} aria-hidden />
          {t(`settings.openclaw.channels.details.${rotate.labelKey}`)}
        </button>
        {/* WA honesty carve-out (the standing blocked item, formerly enforced
            by ChannelsTab's channelHasWorkingDisconnect — moved HERE when that
            tab retired): the runtime's WhatsApp /disconnect does not exist
            yet, so the button renders DISABLED with the honest tooltip. A
            one-touch button that silently no-ops would be worse than the wait. */}
        <button
          type="button"
          onClick={() => {
            setDisconnectError(null);
            setDisconnectDialogOpen(true);
          }}
          disabled={disconnecting || id === "whatsapp"}
          title={
            id === "whatsapp"
              ? t("settings.openclaw.channels.disconnectUnavailableTooltip")
              : undefined
          }
          data-testid={`channel-details-${id}-disconnect`}
          className="ml-auto inline-flex items-center gap-1.5 rounded border border-red-300/60 bg-red-50 px-2 py-1 text-[11px] text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-700/40 dark:bg-red-950/30 dark:text-red-300 dark:hover:bg-red-900/40"
        >
          {disconnecting ? (
            <Loader2 size={11} aria-hidden className="animate-spin" />
          ) : (
            <Unlink size={11} aria-hidden />
          )}
          {t("settings.openclaw.channels.details.disconnect")}
        </button>
      </div>
      {id === "whatsapp" && (
        <p
          className="text-[11px] text-coffee-500 dark:text-cream-500"
          data-testid="whatsapp-disconnect-unavailable"
        >
          {t("settings.openclaw.channels.disconnectUnavailableNote")}
        </p>
      )}

      {lastTest && (
        <p
          data-testid={`channel-details-${id}-test-success`}
          className="flex items-center gap-1 text-[11px] text-emerald-700 dark:text-emerald-300"
        >
          <Check size={11} aria-hidden />
          {t("settings.openclaw.channels.details.lastTest", {
            when: new Date(lastTest.sentAt).toLocaleTimeString(),
            target: lastTest.target,
          })}
        </p>
      )}
      {testError && (
        <p
          data-testid={`channel-details-${id}-test-error`}
          className={cn(
            "flex items-start gap-1 text-[11px]",
            "text-amber-800 dark:text-amber-300",
          )}
        >
          <AlertTriangle size={11} aria-hidden className="mt-0.5 shrink-0" />
          <span>{testError}</span>
        </p>
      )}

      {/* Typed-phrase confirm gate for /disconnect. Renders under the
          panel; portaled outside via the dialog's fixed positioning. */}
      <ConfirmDestructiveDialog
        open={disconnectDialogOpen}
        onClose={() => {
          setDisconnectDialogOpen(false);
          setDisconnectError(null);
        }}
        title={t("settings.openclaw.channels.details.disconnectDialog.title", {
          channel: channelDisplayName(id),
        })}
        description={t("settings.openclaw.channels.details.disconnectDialog.description")}
        bullets={
          t("settings.openclaw.channels.details.disconnectDialog.bullets", {
            returnObjects: true,
          }) as unknown as readonly string[]
        }
        confirmPhrase={t("settings.openclaw.channels.details.disconnectDialog.confirmPhrase")}
        confirmPromptLabel={t("settings.openclaw.channels.details.disconnectDialog.confirmPromptLabel")}
        confirmButtonLabel={t("settings.openclaw.channels.details.disconnectDialog.confirmButtonLabel")}
        cancelButtonLabel={t("settings.openclaw.channels.wizard.cancel")}
        onConfirm={() => handleDisconnectConfirmed()}
        busy={disconnecting}
        error={disconnectError}
        severity="destructive"
      />
    </div>
  );
}

// Local — used ONLY inside the dialog title interpolation. Kept here rather
// than shared: the channel row that used to own a display-name resolver
// (ChannelsTab) was retired in S2d, and this is the only remaining consumer.
function channelDisplayName(id: ChannelId): string {
  switch (id) {
    case "whatsapp":
      return "WhatsApp";
    case "telegram":
      return "Telegram";
    case "email":
      return "Email";
    default:
      return id;
  }
}

function FieldLabel({
  Icon,
  label,
}: {
  Icon: React.ComponentType<{ size?: number; className?: string; "aria-hidden"?: boolean }>;
  label: string;
}) {
  return (
    <dt className="flex items-center gap-1 text-slate-500 dark:text-cream-500">
      <Icon size={10} aria-hidden className="shrink-0" />
      <span>{label}</span>
    </dt>
  );
}
